'use client';
// MarketRules Component - Pure MUI
import { useState } from 'react';
import Link from 'next/link';
import { Box, Button, Card, CardContent, Skeleton, Typography } from '@mui/material';

import { useSafeLanguage } from '@/contexts';
import { useT } from '@/hooks';
import { useMarketRuleTranslation } from '@/services/query/markets.queries';

interface MarketRulesProps {
  description?: string;
  marketId?: string;
}

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

const renderTextWithLinks = (text?: string) => {
  if (!text) return text;

  const parts = text.split(URL_REGEX);
  const handleClick = (e: any) => e.stopPropagation();

  return parts.map((part, index) => {
    if (part.match(URL_REGEX)) {
      return (
        <Link
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="font-medium text-[var(--primary)] transition duration-300 hover:underline"
        >
          Link
        </Link>
      );
    }
    return part;
  });
};

export const MarketRules = ({ description, marketId }: MarketRulesProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useT();
  const { language } = useSafeLanguage();

  const { data: translationData, isLoading: isTranslating } = useMarketRuleTranslation(
    description,
    language?.code,
  );

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const displayDescription = translationData?.translatedText || description;
  const hasLongDescription = displayDescription && displayDescription.length > 200;

  let shortDescription;

  if (isTranslating) {
    shortDescription = '';
  } else if (!displayDescription) {
    shortDescription = t('market.noRules');
  } else if (hasLongDescription) {
    shortDescription = displayDescription.slice(0, 200) + '...';
  } else {
    shortDescription = displayDescription;
  }

  return (
    <Card
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          {t('market.rules')}
        </Typography>
        <Box>
          {isTranslating ? (
            <Box>
              <Skeleton animation="wave" width="100%" height={24} />
              <Skeleton animation="wave" width="100%" height={24} />
              <Skeleton animation="wave" width="80%" height={24} />
            </Box>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {renderTextWithLinks(isExpanded ? displayDescription : shortDescription)}
              </Typography>

              {hasLongDescription && (
                <Button
                  size="small"
                  onClick={handleToggleExpand}
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    textTransform: 'none',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {isExpanded ? t('common.showLess') : t('common.showMore')}
                </Button>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MarketRules;
